import { Suspense } from 'react';
import { Canvas } from '@react-three/fiber';
import { NoToneMapping } from 'three';
import { Link } from '@tanstack/react-router';
import { useAreaConfig } from '@/hooks';
import { Loader } from '@/components/Common/Loader/Loader';
import { Experience } from '@/components/AreaLanding/Experience';
import { PreviewPanel } from '../AddSubjectWizard/components/PreviewPanel';
import { ADMIN_SCROLLBAR_CLASSES } from '../constants';
import { ColoredBadge } from '../components';

interface AreaDetailProps {
  areaSlug: string;
}

export const AreaDetail = ({ areaSlug }: AreaDetailProps) => {
  const { data: areaConfig } = useAreaConfig(areaSlug, 'EDITOR');

  // AdminLayout handles loading and error states before this component renders
  if (!areaConfig) return null;

  return (
    <div className="flex gap-6 px-4 py-6" style={{ height: 'calc(100vh - 56px)' }}>

      {/* Left: scrollable details */}
      <div className={`flex-1 min-w-0 overflow-y-auto space-y-6 pr-2 ${ADMIN_SCROLLBAR_CLASSES}`}>
        <h1 className="text-xl font-bold text-[#E8E8E8] capitalize">{areaConfig.title}</h1>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Details</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex gap-4">
              <dt className="text-[#999] w-36 shrink-0">Slug</dt>
              <dd className="font-mono text-xs text-[#E8E8E8] pt-px">{areaSlug}</dd>
            </div>
            {areaConfig.description && (
              <div className="flex gap-4">
                <dt className="text-[#999] w-36 shrink-0">Description</dt>
                <dd className="text-[#E8E8E8]">{areaConfig.description}</dd>
              </div>
            )}
            <div className="flex gap-4">
              <dt className="text-[#999] w-36 shrink-0">Subject name</dt>
              <dd className="text-[#E8E8E8] capitalize">
                {areaConfig.subjectName.singular} / {areaConfig.subjectName.plural}
              </dd>
            </div>
            <div className="flex gap-4">
              <dt className="text-[#999] w-36 shrink-0">Label name</dt>
              <dd className="text-[#E8E8E8] capitalize">
                {areaConfig.labelName.singular} / {areaConfig.labelName.plural}
              </dd>
            </div>
          </dl>
        </section>

        <section className="flex flex-wrap gap-3">
          <Link
            to="/admin/areas/$areaSlug/subjects"
            params={{ areaSlug }}
            className="px-4 py-2 text-sm rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold hover:bg-[#D4E845] transition-colors capitalize"
          >
            Manage {areaConfig.subjectName.plural} →
          </Link>
          {areaConfig.accessType === 'ADMIN' && (
            <Link
              to="/admin/areas/$areaSlug/acl"
              params={{ areaSlug }}
              className="px-4 py-2 text-sm rounded border border-[#444] text-[#CCC] hover:border-[#C3D82E] hover:text-[#C3D82E] transition-colors"
            >
              Manage Access →
            </Link>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Teaching units</h2>
          {areaConfig.units.length === 0 ? (
            <p className="text-sm text-[#999]">No units configured.</p>
          ) : (
            <div className="space-y-1">
              {areaConfig.units.map((unit) => (
                <div key={unit.id} className="flex gap-4 text-sm">
                  <span className="font-mono text-xs text-[#C3D82E] w-28 shrink-0 pt-px">{unit.udc}</span>
                  <span className="text-[#E8E8E8]">{unit.name}</span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Disciplines</h2>
          {areaConfig.disciplines.length === 0 ? (
            <p className="text-sm text-[#999]">No disciplines configured.</p>
          ) : (
            <div className="space-y-2">
              {areaConfig.disciplines.map((discipline) => (
                <div key={discipline.id} className="flex gap-4 text-sm">
                  <span className="text-[#E8E8E8] font-medium w-36 shrink-0">{discipline.name}</span>
                  {discipline.description && (
                    <span className="text-[#999]">{discipline.description}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Category groups</h2>
          {areaConfig.categoryGroups.length === 0 ? (
            <p className="text-sm text-[#999]">No category groups configured.</p>
          ) : (
            <div className="space-y-4">
              {areaConfig.categoryGroups.map((group) => (
                <div key={group.id} className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-[#CCC]">{group.name}</span>
                    {group.isPrimary && <span className="text-xs text-[#888]">(primary)</span>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {group.categories.map((cat) => (
                      <ColoredBadge key={cat.id} color={cat.color ?? '#999'} dot={!!cat.color}>
                        {cat.name}
                      </ColoredBadge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

      </div>

      {/* Right: Landing page demo */}
      <PreviewPanel heading="Landing page demo">
        {areaConfig.landingDemo ? (
          <Canvas
            gl={{ precision: 'highp', toneMapping: NoToneMapping, autoClear: false }}
            dpr={window.devicePixelRatio}
          >
            <Suspense fallback={<Loader />}>
              <Experience areaConfig={areaConfig} />
            </Suspense>
          </Canvas>
        ) : (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-[#555]">No demo configured.</p>
          </div>
        )}
      </PreviewPanel>

    </div>
  );
};
